import type {
  CrownSolveInput,
  CrownSolveResult,
  Vec3,
} from "../../types/restoration";

type CrownSolverWasmModule = {
  _solve_crown_basic: (
    jawType: number,

    targetCx: number,
    targetCy: number,
    targetCz: number,
    targetW: number,
    targetH: number,
    targetD: number,

    leftCx: number,
    leftCy: number,
    leftCz: number,
    leftW: number,
    leftH: number,
    leftD: number,
    hasLeft: number,

    rightCx: number,
    rightCy: number,
    rightCz: number,
    rightW: number,
    rightH: number,
    rightD: number,
    hasRight: number,

    outPtr: number
  ) => number;

  _malloc: (size: number) => number;
  _free: (ptr: number) => void;
  getValue: (ptr: number, type: string) => number;
};

let wasmModulePromise: Promise<CrownSolverWasmModule> | null = null;

function jawTypeToInt(jawType: CrownSolveInput["jawType"]): number {
  return jawType === "upper" ? 1 : 0;
}

function zeroVec(): Vec3 {
  return [0, 0, 0];
}

async function loadWasmModule(): Promise<CrownSolverWasmModule> {
  if (!wasmModulePromise) {
    wasmModulePromise = import("./crown_solver.js").then(async (mod: any) => {
      const instance = await mod.default();

      console.log("[DCube][WASM] module object:", instance);
      console.log("[DCube][WASM] module keys:", Object.keys(instance || {}));

      return instance as CrownSolverWasmModule;
    });
  }

  return wasmModulePromise;
}

function readDoubleArray(
  wasm: CrownSolverWasmModule,
  ptr: number,
  count: number
): number[] {
  const values: number[] = [];

  for (let i = 0; i < count; i += 1) {
    values.push(wasm.getValue(ptr + i * 8, "double"));
  }

  return values;
}

export async function solveCrownBasic(
  input: CrownSolveInput
): Promise<CrownSolveResult> {
  const wasm = await loadWasmModule();

  const outputValueCount = 9;
  const outputBytes = outputValueCount * Float64Array.BYTES_PER_ELEMENT;
  const outPtr = wasm._malloc(outputBytes);

  try {
    const t = input.target;
    const l = input.leftNeighbor ?? null;
    const r = input.rightNeighbor ?? null;

    const ok = wasm._solve_crown_basic(
      jawTypeToInt(input.jawType),

      t.center[0],
      t.center[1],
      t.center[2],
      t.size[0],
      t.size[1],
      t.size[2],

      l?.center[0] ?? 0,
      l?.center[1] ?? 0,
      l?.center[2] ?? 0,
      l?.size[0] ?? 0,
      l?.size[1] ?? 0,
      l?.size[2] ?? 0,
      l ? 1 : 0,

      r?.center[0] ?? 0,
      r?.center[1] ?? 0,
      r?.center[2] ?? 0,
      r?.size[0] ?? 0,
      r?.size[1] ?? 0,
      r?.size[2] ?? 0,
      r ? 1 : 0,

      outPtr
    );

    if (!ok) {
      return {
        success: false,
        center: t.center,
        rotationEuler: zeroVec(),
        dimensions: t.size,
        message: "WASM crown solver returned failure.",
      };
    }

    const out = readDoubleArray(wasm, outPtr, outputValueCount);

    return {
      success: true,
      center: [out[0], out[1], out[2]],
      rotationEuler: [out[3], out[4], out[5]],
      dimensions: [out[6], out[7], out[8]],
      message: "Crown solved successfully.",
    };
  } catch (error) {
    console.error("[DCube][WASM] solveCrownBasic failed:", error);

    return {
      success: false,
      center: input.target.center,
      rotationEuler: zeroVec(),
      dimensions: input.target.size,
      message:
        error instanceof Error
          ? error.message
          : "Unknown error during WASM crown solve.",
    };
  } finally {
    wasm._free(outPtr);
  }
}